import { NgClass } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { PASSWORD_MIN_LENGTH } from '../../constants/general-constants';

interface PasswordChecks {
    length: boolean;
    containThree: boolean;
    containDetails: {
        lowerCase: boolean;
        upperCase: boolean;
        number: boolean;
        symbol: boolean;
    };
}

//TODO: make this configurable for all types of password inputs
@Component({
    selector: 'app-password-input',
    templateUrl: './password-input.component.html',
    styleUrls: ['./password-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: PasswordInputComponent,
            multi: true,
        },
    ],
    imports: [ReactiveFormsModule, NgClass, TranslateModule],
})
export class PasswordInputComponent implements ControlValueAccessor, AfterViewInit {
    @ViewChild('passwordInput', { read: ElementRef }) input!: ElementRef;

    @Input() id?: string;
    @Input() showHints: boolean = true;

    public onChange!: Function;
    public onTouch!: Function;
    public formGroup = new UntypedFormGroup({ password: new UntypedFormControl('') });
    public showPassword = false;
    public checks: PasswordChecks = {
        length: false,
        containThree: false,
        containDetails: {
            lowerCase: false,
            upperCase: false,
            number: false,
            symbol: false,
        },
    };
    private subscriptions: { [key: string]: Subscription | undefined } = {};

    constructor() {}

    public ngAfterViewInit() {
        this.subscriptions.password = this.formGroup.get('password')?.valueChanges.subscribe((value) => {
            this.computeValidity();
            this.onChange(value);
        });
    }

    public registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.formGroup.get('password')?.[isDisabled ? 'disable' : 'enable']();
    }

    public writeValue(obj: any): void {
        this.formGroup.get('password')?.setValue(obj);
        this.computeValidity();
    }

    private computeValidity() {
        if (!this.showHints) {
            return;
        }

        const password = this.formGroup.get('password')?.value;

        this.checks.length = password.length >= PASSWORD_MIN_LENGTH;
        this.checks.containDetails.lowerCase = password.match(/[a-z]/g)?.length > 0;
        this.checks.containDetails.upperCase = password.match(/[A-Z]/g)?.length > 0;
        this.checks.containDetails.number = password.match(/[0-9]/g)?.length > 0;
        this.checks.containDetails.symbol = password.match(/[^a-zA-Z0-9]/g)?.length > 0;

        this.checks.containThree =
            (['lowerCase', 'upperCase', 'number', 'symbol'].reduce((accumulator, currentValue) => {
                return accumulator + Number((this.checks.containDetails as { [key: string]: boolean })[currentValue]);
            }, 0) as unknown as number) >= 3;
    }
}
